import { AccessModule } from "@shoutout-labs/market_buzz_crm_types";
import { SalesTrendsLockView } from "@/components/reports/sales/SalesTrendsLockView";
import { SalesByDayOfWeekLockView } from "@/components/reports/sales/SalesByDayOfWeekLockView";
import { SalesByTimeOfDayLockView } from "@/components/reports/sales/SalesByTimeOfDayLockView";
import { ProductInsightsLockView } from "@/components/reports/sales/ProductInsightsLockView";
import { CustomerVisitTrendLockView } from "@/components/reports/customers/CustomerVisitTrendLockView";
import { CustomerFunnelJourneyLockView } from "@/components/reports/customers/CustomerFunnelJourneyLockView";
import { RFMAnalysisLockView } from "@/components/reports/customers/RFMAnalysisLockView";
import { RevenueBySegmentLockView } from "@/components/reports/customers/RevenueBySegmentLockView";

/**
 * Feature permission mapping for lock views
 * Maps feature names to their required permissions and lock view components
 */
export interface FeaturePermission {
  feature: string;
  requiredModule?: AccessModule;
  requiredFeature?: string;
  lockViewComponent: React.ComponentType;
  title: string;
  description: string;
}

/**
 * Feature permissions mapping
 * This maps each premium feature to its requirements and lock view component
 */
export const FEATURE_PERMISSIONS: Record<string, FeaturePermission> = {
  // Dashboard Features
  DASHBOARD_CAMPAIGN_ROI: {
    feature: "DASHBOARD_CAMPAIGN_ROI",
    requiredFeature: "DASHBOARD_CAMPAIGN_ROI",
    lockViewComponent: () => null,
    title: "Campaign ROI Dashboard",
    description: "View campaign ROI metrics on dashboard"
  },

  // Sales Reports Premium Features (require SALES_REPORTS_FULL_VIEW)
  SALES_REPORT_TRENDS: {
    feature: "SALES_REPORT_TRENDS",
    requiredFeature: "SALES_REPORTS_FULL_VIEW",
    lockViewComponent: SalesTrendsLockView,
    title: "Sales Trends & Patterns",
    description: "Advanced year-over-year comparison and trend analysis"
  },
  SALES_REPORT_DAY_OF_WEEK: {
    feature: "SALES_REPORT_DAY_OF_WEEK",
    requiredFeature: "SALES_REPORTS_FULL_VIEW",
    lockViewComponent: SalesByDayOfWeekLockView,
    title: "Sales by Day of Week",
    description: "Analyze sales patterns by day of the week"
  },
  SALES_REPORT_TIME_OF_DAY: {
    feature: "SALES_REPORT_TIME_OF_DAY",
    requiredFeature: "SALES_REPORTS_FULL_VIEW",
    lockViewComponent: SalesByTimeOfDayLockView,
    title: "Sales by Time of Day",
    description: "Analyze sales patterns by time of day"
  },
  SALES_REPORT_PRODUCT_INSIGHTS: {
    feature: "SALES_REPORT_PRODUCT_INSIGHTS",
    requiredFeature: "SALES_REPORTS_FULL_VIEW",
    lockViewComponent: ProductInsightsLockView,
    title: "Top 5 Products by Revenue",
    description: "Top products by revenue and performance analysis"
  },

  // Customer Reports Premium Features (require CUSTOMERS_REPORTS_FULL_VIEW)
  CUSTOMER_REPORT_VISIT_TREND: {
    feature: "CUSTOMER_REPORT_VISIT_TREND",
    requiredFeature: "CUSTOMERS_REPORTS_FULL_VIEW",
    lockViewComponent: CustomerVisitTrendLockView,
    title: "Customer Visit Trend",
    description: "Track customer visit patterns and engagement frequency"
  },
  CUSTOMER_REPORT_FUNNEL_JOURNEY: {
    feature: "CUSTOMER_REPORT_FUNNEL_JOURNEY",
    requiredFeature: "CUSTOMERS_REPORTS_FULL_VIEW",
    lockViewComponent: CustomerFunnelJourneyLockView,
    title: "Funnel Journey Monthly Trends",
    description: "Analyze customer journey funnel and conversion rates"
  },
  CUSTOMER_REPORT_RFM_ANALYSIS: {
    feature: "CUSTOMER_REPORT_RFM_ANALYSIS",
    requiredFeature: "CUSTOMERS_REPORTS_FULL_VIEW",
    lockViewComponent: RFMAnalysisLockView,
    title: "RFM Analysis",
    description: "Segment customers using RFM analysis for targeted marketing"
  },
  CUSTOMER_REPORT_SEGMENT_REVENUE: {
    feature: "CUSTOMER_REPORT_SEGMENT_REVENUE",
    requiredFeature: "CUSTOMERS_REPORTS_FULL_VIEW",
    lockViewComponent: RevenueBySegmentLockView,
    title: "Revenue by Customer Segment",
    description: "Analyze revenue distribution across customer segments"
  }
};

/**
 * Check if user has access to a specific feature
 * @param featureKey - The feature key to check
 * @param userFeatures - Array of features the user has access to
 * @param userModules - Array of modules the user has access to
 * @returns boolean - True if user has access to the feature
 */
export const hasFeatureAccess = (
  featureKey: string,
  userFeatures: string[],
  userModules: AccessModule[]
): boolean => {
  const featurePermission = FEATURE_PERMISSIONS[featureKey];

  if (!featurePermission) {
    return true; // If no permission defined, allow access
  }

  // Check if user has the required feature
  if (featurePermission.requiredFeature) {
    return userFeatures.includes(featurePermission.requiredFeature);
  }

  // Fallback to module check
  if (featurePermission.requiredModule) {
    return userModules.includes(featurePermission.requiredModule);
  }

  return true; // If no requirements, allow access
};

/**
 * Get the lock view component for a feature if user doesn't have access
 * @param featureKey - The feature key to get lock view for
 * @param userFeatures - Array of features the user has access to
 * @param userModules - Array of modules the user has access to
 * @returns React.ComponentType | null - Lock view component or null if user has access
 */
export const getLockViewForFeature = (
  featureKey: string,
  userFeatures: string[],
  userModules: AccessModule[]
): React.ComponentType | null => {
  if (hasFeatureAccess(featureKey, userFeatures, userModules)) {
    return null; // User has access, no lock view needed
  }

  const featurePermission = FEATURE_PERMISSIONS[featureKey];
  return featurePermission?.lockViewComponent || null;
};

/**
 * Get all features that user doesn't have access to
 * @param userFeatures - Array of features the user has access to
 * @param userModules - Array of modules the user has access to
 * @returns string[] - Array of feature keys user doesn't have access to
 */
export const getInaccessibleFeatures = (
  userFeatures: string[],
  userModules: AccessModule[]
): string[] => {
  return Object.keys(FEATURE_PERMISSIONS).filter(
    (featureKey) => !hasFeatureAccess(featureKey, userFeatures, userModules)
  );
};

/**
 * Get all features that user has access to
 * @param userFeatures - Array of features the user has access to
 * @param userModules - Array of modules the user has access to
 * @returns string[] - Array of feature keys user has access to
 */
export const getAccessibleFeatures = (
  userFeatures: string[],
  userModules: AccessModule[]
): string[] => {
  return Object.keys(FEATURE_PERMISSIONS).filter((featureKey) =>
    hasFeatureAccess(featureKey, userFeatures, userModules)
  );
};
